import {
  labelOf,
  PAYMENT_METHODS,
  type Paginated,
  type Payment,
  type ReceivablesSummary,
} from '@facture/contracts'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { AlertTriangleIcon, ClockIcon, WalletIcon } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { z } from 'zod'
import { Badge } from '@/components/ui/badge'
import { Card } from '@/components/ui/card'
import { columnHelper, DataTable, useDebounced } from '@/components/ui/data-table'
import {
  ClearFilters,
  FilterBar,
  FilterDateRange,
  FilterSearch,
  FilterSelect,
} from '@/components/ui/filter-bar'
import { EmptyState, Page, PageHeader } from '@/components/ui/page'
import { api } from '@/lib/api'
import { formatDate, formatMoney } from '@/lib/format'
import { cn } from '@/lib/utils'

const searchSchema = z.object({
  q: z.string().optional(),
  method: z.string().optional(),
  status: z.enum(['active', 'voided', 'all']).optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  page: z.number().int().min(1).optional(),
})

export const Route = createFileRoute('/_app/payments')({
  staticData: { crumb: 'Cobros' },
  validateSearch: searchSchema,
  component: PaymentsPage,
})

const col = columnHelper<Payment>()

const AGING_LABEL: Record<string, string> = {
  current: 'Al día',
  '1-30': '1–30 días',
  '31-60': '31–60 días',
  '61-90': '61–90 días',
  '90+': 'Más de 90',
}

function PaymentsPage() {
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const [text, setText] = useState(search.q ?? '')
  const q = useDebounced(text)
  const setSearch = (patch: Partial<z.infer<typeof searchSchema>>) =>
    navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true })

  // biome-ignore lint/correctness/useExhaustiveDependencies: solo reacciona al texto debounced
  useEffect(() => {
    if (q !== (search.q ?? '')) setSearch({ q: q || undefined, page: undefined })
  }, [q])

  const query = {
    search: search.q,
    method: search.method,
    status: search.status ?? 'active',
    from: search.from,
    to: search.to,
    page: search.page ?? 1,
    pageSize: 25,
  }
  const list = useQuery({
    queryKey: ['payments', query],
    queryFn: () => api.get<Paginated<Payment>>('/payments', { query }),
    placeholderData: keepPreviousData,
  })
  const summary = useQuery({
    queryKey: ['receivables', 'summary'],
    queryFn: () => api.get<ReceivablesSummary>('/receivables/summary'),
  })
  const maxBucket = Math.max(1, ...(summary.data?.aging.map((a) => a.amountCrc) ?? [1]))

  const columns = useMemo(
    () => [
      col.accessor('paidAt', {
        header: 'Fecha',
        cell: ({ getValue }) => <span className="tabular">{formatDate(getValue())}</span>,
      }),
      col.accessor('customerName', {
        header: 'Cliente',
        cell: ({ row }) => (
          <div>
            <div className="font-medium">{row.original.customerName ?? '—'}</div>
            <Link
              to="/documents/$id"
              params={{ id: row.original.documentId }}
              className="text-primary text-xs hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              Factura #{row.original.documentConsecutive?.slice(10).replace(/^0+/, '')}
            </Link>
          </div>
        ),
      }),
      col.accessor('method', {
        header: 'Medio',
        meta: { hideOnMobile: true },
        cell: ({ row }) => (
          <div>
            <div>{labelOf(PAYMENT_METHODS, row.original.method)}</div>
            {row.original.bankAccountName && (
              <div className="text-muted-foreground text-xs">{row.original.bankAccountName}</div>
            )}
          </div>
        ),
      }),
      col.accessor('reference', {
        header: 'Referencia',
        meta: { hideOnMobile: true },
        cell: ({ getValue }) => (
          <span className="font-mono text-muted-foreground text-xs">{getValue() ?? '—'}</span>
        ),
      }),
      col.accessor('amount', {
        header: 'Monto',
        meta: { align: 'right' },
        cell: ({ row }) => (
          <div>
            <div className={cn('font-medium', row.original.voidedAt && 'line-through opacity-60')}>
              {formatMoney(row.original.amount, row.original.currency as 'CRC')}
            </div>
            {row.original.voidedAt && <Badge variant="destructive">Anulado</Badge>}
          </div>
        ),
      }),
    ],
    [],
  )

  return (
    <Page>
      <PageHeader
        title="Cobros y cartera"
        description="Pagos recibidos y lo que tus clientes te deben, por antigüedad."
      />

      <div className="mb-6 grid gap-4 lg:grid-cols-[18rem_minmax(0,1fr)]">
        <Card className="relative overflow-hidden p-5">
          <div className="pointer-events-none absolute -top-12 -right-12 size-40 rounded-full bg-primary/15 blur-2xl" />
          <div className="relative">
            <div className="flex items-center gap-2 text-muted-foreground text-sm">
              <WalletIcon className="size-4" /> Por cobrar
            </div>
            <div className="mt-2 font-semibold text-3xl tracking-tight tabular">
              {formatMoney(summary.data?.totalCrc ?? 0)}
            </div>
            <div className="mt-1 text-muted-foreground text-xs">
              {summary.data?.documents ?? 0}{' '}
              {summary.data?.documents === 1 ? 'factura con saldo' : 'facturas con saldo'}
            </div>
            {(summary.data?.overdueCrc ?? 0) > 0 && (
              <div className="mt-4 flex items-center gap-2 rounded-lg bg-destructive-soft px-3 py-2 text-destructive-soft-foreground text-sm">
                <AlertTriangleIcon className="size-4" /> {formatMoney(summary.data?.overdueCrc ?? 0)} vencido
              </div>
            )}
          </div>
        </Card>
        <Card className="p-5">
          <div className="mb-4 flex items-center gap-2 text-muted-foreground text-sm">
            <ClockIcon className="size-4" /> Antigüedad de saldos
          </div>
          <div className="grid gap-3">
            {summary.data?.aging.map((a) => (
              <div key={a.bucket} className="grid grid-cols-[6.5rem_1fr_8rem] items-center gap-3 text-sm">
                <span className="text-muted-foreground">{AGING_LABEL[a.bucket]}</span>
                <div className="h-2 overflow-hidden rounded-full bg-muted">
                  <div
                    className={cn(
                      'h-full rounded-full transition-all',
                      a.bucket === 'current'
                        ? 'bg-success'
                        : a.bucket === '1-30'
                          ? 'bg-warning'
                          : 'bg-destructive',
                    )}
                    style={{ width: `${(a.amountCrc / maxBucket) * 100}%` }}
                  />
                </div>
                <span className="text-right tabular">{formatMoney(a.amountCrc)}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <DataTable
        columns={columns}
        data={list.data?.items}
        loading={list.isFetching}
        getRowId={(p) => p.id}
        onRowClick={(p) => navigate({ to: '/documents/$id', params: { id: p.documentId } })}
        toolbar={
          <FilterBar>
            <FilterSearch value={text} onChange={setText} placeholder="Buscar por cliente o factura…" />
            <FilterSelect
              label="Medio de pago"
              width="lg"
              value={search.method ?? 'all'}
              onValueChange={(v) => setSearch({ method: v === 'all' ? undefined : v, page: undefined })}
              options={[
                { value: 'all', label: 'Todos los medios' },
                ...PAYMENT_METHODS.map((m) => ({ value: m.code, label: m.name })),
              ]}
            />
            <FilterSelect
              label="Vigentes o anulados"
              width="sm"
              value={search.status ?? 'active'}
              onValueChange={(v) => setSearch({ status: v as 'active', page: undefined })}
              options={[
                { value: 'active', label: 'Vigentes' },
                { value: 'voided', label: 'Anulados' },
                { value: 'all', label: 'Todos' },
              ]}
            />
            <FilterDateRange
              value={{ from: search.from, to: search.to }}
              onChange={(r) => setSearch({ from: r.from, to: r.to, page: undefined })}
            />
            <ClearFilters
              active={
                !!(
                  search.q ||
                  search.method ||
                  (search.status && search.status !== 'active') ||
                  search.from ||
                  search.to
                )
              }
              onClear={() => {
                setText('')
                setSearch({
                  q: undefined,
                  method: undefined,
                  status: undefined,
                  from: undefined,
                  to: undefined,
                  page: undefined,
                })
              }}
            />
          </FilterBar>
        }
        empty={
          <EmptyState
            icon={WalletIcon}
            title="Sin cobros"
            description="Registrá cobros desde el detalle de cada factura aceptada."
          />
        }
        pagination={
          list.data && {
            page: list.data.page,
            pageSize: list.data.pageSize,
            total: list.data.total,
            onPageChange: (p) => setSearch({ page: p === 1 ? undefined : p }),
          }
        }
      />
    </Page>
  )
}
